/**
 * Sentry / GlitchTip initialization.
 *
 * GlitchTip is event-API-compatible with Sentry, so `@sentry/react` works
 * unchanged — point the DSN at the GlitchTip host.
 *
 * Soft-loaded: when `VITE_SENTRY_DSN` is empty (the default in tests and
 * fresh dev clones), this module is a no-op and `@sentry/react` is never
 * imported. That keeps the prod bundle from paying the SDK weight in
 * deploys that have not enabled error tracking yet.
 *
 * See docs/RUNBOOK.md section 11 for the operator procedure (create
 * project in GlitchTip UI -> SDK Setup -> copy DSN -> set in `.env`).
 */

export async function initSentry(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  // Dynamic import so a missing/disabled SDK never blocks the bundle.
  const Sentry = await import("@sentry/react");
  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? "dev",
    release: import.meta.env.VITE_SENTRY_RELEASE,
    // Errors-only by default. Bump to 0.1 only if performance traces are
    // actually being looked at — GlitchTip's transaction store is small.
    tracesSampleRate: 0,
    // Default-deny PII: the FE never needs to ship user IPs / cookies to
    // error tracking, and ADR-009 / audit-log forbid logging decrypted
    // secrets. Operators investigating an error get the stack trace plus
    // explicit `Sentry.setUser({...})` data only.
    sendDefaultPii: false,
  });
}
